import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { UploadFileResponse } from '../models/documents.model';
import { LocalStorageService } from './local-storage.service';

@Injectable({
	providedIn: 'root',
})
export class FilesService {
	constructor(
		private readonly http: HttpClient,
		private localStorage: LocalStorageService
	) {}

	public getDocumentsTypes(userId?: any): Observable<any> {
		return this.http.get<any>(`${environment.apiUrl}files/users/${userId ? userId : this.userId}/documents/types`);
	}

	public getUploadedFiles(userId: string) {
		return this.http.get<any>(`${environment.apiUrl}files/users/${userId ? userId : this.userId}/documents/all`);
	}

	getReciboCaja(contractId: string, fileType: string, transactionId?: string) {
    let url = `${environment.apiUrl}files/contracts/${contractId}/documents/${fileType}/advancedPayment`;
    if (transactionId) {
        url += `?number=${transactionId}`;
    }
    return this.http.get(url, { responseType: 'arraybuffer' });
	}

	getCashReceipt(contractId: string): Observable<Blob> {
		const url = `${environment.apiUrl}payments/${contractId}/cash-receipt`;
		return this.http.get(url, { responseType: 'blob' });
	}

	public uploadDocument(type: number, file: any, documentId?: number, userId?: string): Observable<{ data: UploadFileResponse }> {
		const formData = new FormData();
		formData.set('file', file);
		const queryString = documentId ? `?pendencyId=${documentId}` : '';
		let userIdReq = userId ? userId : this.userId;
		return this.http.put<{ data: UploadFileResponse }>(`${environment.apiUrl}files/users/${userIdReq}/documents/${type}/uploads` + queryString, formData);
	}

	public uploadMultipleDocuments(type: number, file: any, documentId?: number, userId?: string): Observable<{ data: UploadFileResponse }> {
		const formData = new FormData();
		formData.set('file', file);
		const queryString = documentId ? `?pendencyId=${documentId}` : '';
		let userIdReq = userId ? userId : this.userId;
		return this.http.put<{ data: UploadFileResponse }>(`${environment.apiUrl}files/users/${userIdReq}/documents/${type}/uploads/multiple` + queryString, formData);
	}

	public uploadChecklist(contractId: string, deviceId: string, file: File, date: string): Observable<any> {
		let formData = new FormData();
		formData.set('ChecklistFile', file);

		return this.http.post<any>(`${environment.apiUrl}files/contracts/${contractId}/devices/${deviceId}/checklists?DeliveryDate=${date}`, formData);
	}

	public uploadDevolutionTerms(contractId: string, file: File): Observable<any> {
		let formData = new FormData();
		formData.set('File', file);

		return this.http.post<any>(`${environment.apiUrl}files/contracts/${contractId}/devolution-terms`, formData);
	}

	public generateKaPdf(clientId: string, contractId: string, itemIds: string[]): Observable<any> {
		const params = itemIds.map(itemId => `idContractVehicleScrap=${itemId}`).join('&');

		const url = `${environment.apiUrl}files/contracts/generate-pdf-contract?systemType=1&contractId=${contractId}&userId=${this.userId}&idClientUser=${clientId}&${params}`;

		return this.http.get<any>(url);
	}

	public downloadBase64File(base64: string, contentType: string, fileName: string): void {
		const byteCharacters = atob(base64);
		const byteArray = new Uint8Array(byteCharacters.length);
		for (let i = 0; i < byteCharacters.length; i++) {
			byteArray[i] = byteCharacters.charCodeAt(i);
		}
		const blob = new Blob([byteArray], { type: contentType });

		const link = document.createElement('a');
		link.href = URL.createObjectURL(blob);
		link.download = fileName || 'download';
		link.click();
	}

	public generateReportsPdf(clientId: any, systemType: string, payload: any, isExcel: boolean): Observable<any> {
		let params = new HttpParams();

		const formatDate = (date: string): string | null => {
			let day: string, month: string, year: string;

			if (!date) {
				return null;
			}
			if (date?.includes('/')) {
				[day, month, year] = date.split('/');
			} else if (date.length === 8) {
				day = date.slice(0, 2);
				month = date.slice(2, 4);
				year = date.slice(4, 8);
			} else {
				return null;
			}

			const formattedDate = new Date(`${year}-${month}-${day}T00:00:00Z`);
			return isNaN(formattedDate.getTime()) ? null : formattedDate.toISOString().slice(0, 19);
		};

		const queryParams = {
			systemType,
			format: isExcel ? 'excel' : null,
			userId: clientId,
			startDate: formatDate(payload.startDate),
			endDate: formatDate(payload.endDate),
			order: payload.order,
			plate: payload.plate,
			type: payload.typeService,
			city: payload.city,
		};

		Object.entries(queryParams).forEach(([key, value]) => {
			if (value != null) {
				params = params.set(key, value.toString());
			}
		});

		return this.http.get<any>(`${environment.apiUrl}files/contracts/generate-pdf-contract`, { params });
	}

	public getContractFile(contractId: string, fileType: number): Observable<any> {
		return this.http.get<any>(`${environment.apiUrl}files/contracts/${contractId}/documents/${fileType}`, {
			observe: 'response',
			responseType: 'blob' as 'json',
		});
	}

	public getDeviceFile(contractId: string, deviceId: string, fileType: number): Observable<HttpResponse<Blob>> {
		return this.http.get(`${environment.apiUrl}files/contracts/${contractId}/devices/${deviceId}/documents/${fileType}`, {
			observe: 'response',
			responseType: 'blob',
		});
	}

  public getUserDocumentFile(userId: string, type: number): Observable<HttpResponse<Blob>> {
    return this.http.get(`${environment.apiUrl}files/users/${userId}/documents/${type}`, {
      observe: 'response',
      responseType: 'blob',
    });
  }

  public getBlobFile(fileName: string): Observable<HttpResponse<Blob>> {
    const encoded = encodeURIComponent(fileName);
    return this.http.get(`${environment.apiUrl}files/blobs/${encoded}`, {
      observe: 'response',
      responseType: 'blob',
    });
  }

  public getBlobFileWithProgress(fileName: string) {
    const encoded = encodeURIComponent(fileName);
    return this.http.get(`${environment.apiUrl}files/blobs/${encoded}`, {
      observe: 'events',
      reportProgress: true,
      responseType: 'blob',
    });
  }

  public deleteBlobFile(fileName: string): Observable<any> {
    const encoded = encodeURIComponent(fileName);
    return this.http.delete(`${environment.apiUrl}files/blobs/${encoded}`);
  }

  public deleteBlobFileById(id: number): Observable<any> {
    return this.http.delete(`${environment.apiUrl}files/blobfiles/${id}`);
  }

  /**
   * Elimina un archivo de forma transaccional: primero de la base de datos y luego del blob storage.
   * Si alguna operación falla, se aborta la siguiente.
   * @param fileName Nombre del archivo en el blob storage
   * @param blobFileId ID del registro en la tabla BlobFiles (opcional)
   * @returns Observable que emite true si ambas operaciones fueron exitosas, false en caso contrario
   */
  public deleteBlobFileTransactional(fileName: string, blobFileId?: number | null): Observable<boolean> {
    return new Observable(observer => {
      // Si tenemos el ID, usar DeleteBlobFileById que elimina de BD y blob storage
      if (blobFileId !== null && blobFileId !== undefined) {
        this.deleteBlobFileById(blobFileId).subscribe({
          next: (response) => {
            console.log('Archivo eliminado correctamente (BD y blob storage):', blobFileId);
            observer.next(true);
            observer.complete();
          },
          error: (err) => {
            console.error('Error al eliminar archivo por ID:', err);
            observer.error({
              message: 'Error al eliminar archivo',
              dbDeleted: false,
              error: err
            });
          }
        });
      } else {
        // Si no hay ID, usar DeleteBlobFile que busca en BD por nombre y elimina de ambos lugares
        this.deleteBlobFile(fileName).subscribe({
          next: (response) => {
            console.log('Archivo eliminado del blob storage (y BD si se encontró):', fileName);
            observer.next(true);
            observer.complete();
          },
          error: (blobErr) => {
            console.error('Error al eliminar archivo del blob storage:', blobErr);
            observer.error({
              message: 'Error al eliminar archivo del blob storage',
              dbDeleted: false,
              blobError: blobErr
            });
          }
        });
      }
    });
  }

	public get user(): void {
		let user: any = this.localStorage.getItem('ROD-logged.user');
		if (user == undefined && user == null && typeof user == 'string') {
			user = JSON.parse(user);
		}
		return user;
	}

	get userId() {
		let user: any = this.localStorage.getItem('ROD-logged.user');
		let clientDealer: any = this.localStorage.getItem('ROD-logged.clientUser') || this.localStorage.getItem('ROD-register.clientUser');
		if (clientDealer !== null && clientDealer !== undefined && typeof clientDealer == 'string') {
			clientDealer = JSON.parse(clientDealer);
			return clientDealer?.authContext?.id || clientDealer?.id || clientDealer?.data?.id;
		} else if (user !== undefined && user !== null && typeof user == 'string') {
			user = JSON.parse(user);
			return user?.authContext?.id || user?.id || user?.data?.id;
		}
	}
}
